import React, { useEffect, useMemo } from 'react';

import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import useForm from 'src/hooks/useForm';
import {
  createAccommodation,
  setBackgroundFormData,
  setBackgroundFormErrors,
  setIsCancelModalOpen,
  setIsProviderPlacedOffWorkModalOpen,
  setJmrFlowType,
  setPageProgress,
  setShowCaseIdRequiredText,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type BackgroundFormFieldLength } from 'src/store/newJmrPageSlice/types';
import { type BackgroundFormData } from 'src/store/newJmrPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { ApiCallStatus } from 'src/store/type';
import { isWithinRange } from 'src/utils/helpers';
import { CaseSources, JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';

import './backgroundPage.scss';
import ProviderPlacedOffWorkModal from './providerPlacedOffWorkModal';

import AssociateInformationAUSTIN from './associateInformation/associateInformationAUSTIN';

/**
 * Main Component
 */
const BackgroundPageAUSTIN = () => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    jmrFlowType,
    backgroundFormData,
    backgroundFormErrors,
    backgroundFormFieldLengths,
    searchedAlias,
    isAUSTINDown,
    accommodationSavingStatus,
  } = useAppSelector((state) => state.newJmrPage);

  // Setup form validation
  const { validateForm, setFormValues, errors } = useForm<BackgroundFormData>(
    getFormFields(backgroundFormFieldLengths, backgroundFormData, featureFlags.DECISION_PORTAL, isAUSTINDown),
  );

  // Handle updates to background form data
  const handleChange = (key: keyof BackgroundFormData, value: string) => {
    // TODO setBackgroundFormData was changed from a replace to a patch operation. This should have no effective impact
    // in this logic but spreading the existing data is now redundant
    let modifiedFormData = { ...backgroundFormData };
    const modifiedFormErrors = { ...backgroundFormErrors };
    // If changing the "site" location
    if (key === 'siteLocation') {
      modifiedFormData = {
        ...backgroundFormData,
        caseID: '',
        cannotWorkMoreThan40Hrs: false,
        cannotWorkNormalShiftHrs: false,
        indefiniteRestrictions: false,
        providerPlacedOffWork: false,
        limitedHoursPerDay: '',
        limitedHoursPerWeek: '',
      };
      // Clear out the errors on these 2 properties when site location changes.
      // The reason is that these checkboxes will become unchecked when the site location
      // changes.
      modifiedFormErrors.limitedHoursPerDay = undefined;
      modifiedFormErrors.limitedHoursPerWeek = undefined;
      // Need to set the string value and the full site object.
      const siteInAllowedSites = allowedSites.find((s) => s.site === value);
      if (!!value && value !== backgroundFormData.site?.site && !!siteInAllowedSites) {
        modifiedFormData = {
          ...modifiedFormData,
          site: siteInAllowedSites,
          siteLocation: value,
        };
      }
    }
    // If changing the "alias"
    if (backgroundFormData.alias !== searchedAlias) {
      modifiedFormData = {
        ...backgroundFormData,
        caseID: '',
        siteLocation: '',
        shift: '',
        shiftLength: 0,
        restrictionEndDate: '',
        restrictionStartDate: '',
        cannotWorkMoreThan40Hrs: false,
        cannotWorkNormalShiftHrs: false,
        indefiniteRestrictions: false,
        providerPlacedOffWork: false,
        limitedHoursPerDay: '',
        limitedHoursPerWeek: '',
      };
    }
    dispatch(setBackgroundFormData({ ...modifiedFormData, [key]: value }));
    dispatch(setBackgroundFormErrors({ ...modifiedFormErrors, [key]: undefined }));
    dispatch(setShowCaseIdRequiredText(false));
  };

  // Set form values
  useEffect(() => {
    if (backgroundFormData) {
      setFormValues(backgroundFormData);
    }
  }, [backgroundFormData]);

  // Set Error toast
  useEffect(() => {
    dispatch(setBackgroundFormErrors(errors));
    const caseSourceError = errors?.caseSource;
    if (caseSourceError?.error) {
      dispatch(
        createToast({
          type: 'error',
          message: caseSourceError?.errorMessage,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [dispatch, errors]);

  // Open provider placed off work modal
  useEffect(() => {
    if (accommodationSavingStatus === ApiCallStatus.Success) {
      dispatch(setIsProviderPlacedOffWorkModalOpen(true));
    }
  }, [accommodationSavingStatus, dispatch]);

  // Setup title String
  const titleStr = useMemo(() => {
    if (jmrFlowType !== JmrFlowTypes.CreateNewAccommodation) {
      if (backgroundFormData.alias) {
        return `Update JMR ${backgroundFormData.alias}`;
      } else {
        return 'Update JMR';
      }
    } else {
      return '';
    }
  }, [jmrFlowType, backgroundFormData.alias]);

  return (
    <NewJMRPageWrapper>
      {/* Main Content */}
      <NewJMRPageMainBody
        title={titleStr}
        xs={{ width: '100%', spacingInset: 'none' }}
        sm={{ width: '100%', spacingInset: '400' }}
        md="90%"
        lg="80%"
        xl="60%"
      >
        <AssociateInformationAUSTIN handleChange={handleChange} />
        <ProviderPlacedOffWorkModal />
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter>
        <BackgroundFormFooterBtns validateForm={validateForm} />
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

/**
 * Footer buttons
 * @returns
 */
const BackgroundFormFooterBtns = (props: { validateForm: () => boolean }) => {
  const { validateForm } = props;
  const dispatch = useAppDispatch();
  const { backgroundFormData, backgroundFormErrors, searchedAlias, accommodationSavingStatus } = useAppSelector(
    (state) => state.newJmrPage,
  );

  const handleNextClick = () => {
    if (validateForm()) {
      if (backgroundFormData.providerPlacedOffWork) {
        dispatch(setJmrFlowType(JmrFlowTypes.ProviderPlacedOffWork));
        dispatch(createAccommodation());
      } else {
        dispatch(setPageProgress(JmrPageProgress.Restrictions));
      }
    } else if (!!backgroundFormErrors.caseID?.error && backgroundFormData.caseSource === CaseSources.Austin) {
      dispatch(
        createToast({
          type: 'error',
          message: 'Case ID is required',
          timeout: TOAST_TIMEOUT,
        }),
      );
      dispatch(setShowCaseIdRequiredText(true));
    }
  };

  const disabled = !(backgroundFormData.fullName && backgroundFormData.alias === searchedAlias);

  return (
    <div
      style={{
        ...(disabled
          ? {
              opacity: 0.25,
              pointerEvents: 'none',
            }
          : {}),
      }}
    >
      <Row spacing="400">
        <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
          Cancel
        </Button>
        <Button data-cy="new-jmr__background__next" onClick={handleNextClick}>
          {backgroundFormData.providerPlacedOffWork && accommodationSavingStatus === ApiCallStatus.Pending ? (
            <Loader size="small" />
          ) : (
            'Next'
          )}
        </Button>
      </Row>
    </div>
  );
};

/**
 * Get the fields for the "useForm" hook
 */
const getFormFields = (
  backgroundFormFieldLengths: BackgroundFormFieldLength,
  backgroundFormData: BackgroundFormData,
  isDPIntegrationEnabled: boolean,
  isAUSTINDown?: boolean,
) => {
  const {
    maxAliasLength,
    maxFullNameLength,
    maxJobLength,
    maxEmployeeIdLength,
    maxShiftCodeLength,
    maxManagerAliasLength,
    maxManagerNameLength,
    maxCaseIdLength,
  } = backgroundFormFieldLengths;
  const {
    indefiniteRestrictions,
    site,
    restrictionStartDate,
    restrictionEndDate,
    cannotWorkMoreThan40Hrs,
    limitedHoursPerWeek,
  } = backgroundFormData;
  return {
    validations: {
      alias: {
        required: { value: true, message: 'Alias is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxAliasLength,
          message: `Alias must be less than or equal to ${maxAliasLength} characters.`,
        },
      },
      fullName: {
        required: { value: true, message: 'Full name is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxFullNameLength,
          message: `Full name must be less than or equal to ${maxFullNameLength} characters.`,
        },
      },
      siteLocation: {
        required: { value: true, message: 'Site location is required' },
      },
      job: {
        required: { value: true, message: 'Job is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxJobLength,
          message: `Job must be less than or equal to ${maxJobLength} characters.`,
        },
      },
      employeeID: {
        required: { value: true, message: 'Employee ID is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxEmployeeIdLength,
          message: `EmployeeID must be less than or equal to ${maxEmployeeIdLength} characters.`,
        },
      },
      shift: {
        required: { value: true, message: 'Shift is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxShiftCodeLength,
          message: `Shift code must be less than or equal to ${maxShiftCodeLength} characters.`,
        },
      },
      shiftLength: {
        custom: {
          isValid: () => +backgroundFormData.shiftLength <= 12 && +backgroundFormData.shiftLength > 0,
          message:
            +backgroundFormData.shiftLength > 12
              ? 'Shift length cannot be more than 12 hrs'
              : 'Shift length must be a number greater than 0',
        },
      },
      managerAlias: {
        required: { value: true, message: 'Manager alias is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxManagerAliasLength,
          message: `Manager alias must be less than or equal to ${maxManagerAliasLength} characters.`,
        },
      },
      managerName: {
        required: { value: true, message: 'Manager name is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxManagerNameLength,
          message: `Manager name must be less than or equal to ${maxManagerNameLength} characters.`,
        },
      },
      caseID: {
        required: { value: isAUSTINDown ? false : true, message: 'Case ID is required' },
        custom: {
          isValid: (value: string) => !value || value.length <= maxCaseIdLength,
          message: `Case ID must be less than or equal to ${maxCaseIdLength} characters.`,
        },
      },
      caseSource: {
        required: { value: true, message: 'Case Source is required' },
      },
      restrictionStartDate: {
        required: { value: true, message: 'Start date is required' },
      },
      restrictionEndDate: {
        custom: {
          isValid: () => {
            return (
              (indefiniteRestrictions && site?.participatingInDecisionHubPilot) ||
              new Date(restrictionStartDate) <= new Date(restrictionEndDate)
            );
          },
          message: 'End date must be later than start date',
        },
      },
      limitedHoursPerDay: {
        custom: {
          isValid: () => isLimitedHoursPerDayValid(backgroundFormData, isDPIntegrationEnabled),
          message:
            isDPIntegrationEnabled && site?.participatingInDecisionHubPilot
              ? 'Must be a number between 1 and normal shift length'
              : 'Must be a number between 0 and normal shift length',
        },
      },
      limitedHoursPerWeek: {
        custom: {
          isValid: () =>
            !cannotWorkMoreThan40Hrs || // cannotWorkMoreThan40Hrs box is not checked
            isWithinRange(limitedHoursPerWeek, 0, 40),
          message: 'Must be a number between 0 and 40',
        },
      },
    },
  };
};

/**
 * Validate "limiteHoursPerDay" field
 */
const isLimitedHoursPerDayValid = (backgroundFormData: BackgroundFormData, isDPIntegrationEnabled: boolean) => {
  // Cannot work normal shift hrs box not checked
  if (!backgroundFormData.cannotWorkNormalShiftHrs) {
    return true;
  }
  // If dp feature flag is on and site is participating, then 0 is not a valid value
  if (isDPIntegrationEnabled && backgroundFormData.site?.participatingInDecisionHubPilot) {
    return isWithinRange(backgroundFormData.limitedHoursPerDay, 1, +backgroundFormData.shiftLength);
  }
  // If dp feature flag is off or site isn't participating, then 0 is a valid value
  return isWithinRange(backgroundFormData.limitedHoursPerDay, 0, +backgroundFormData.shiftLength);
};

export default BackgroundPageAUSTIN;
